import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Icon } from './Icon';
import { BrandSwitcher } from './BrandSwitcher';
import { SpendMeter } from './SpendMeter';
import { useBrand } from '../lib/brand';
import { useAuth, useSessionUser } from '../lib/auth';
import { NAV_GROUPS, PAGES, pathFor, type PageDef } from '../lib/nav';
import { useAttentionCount, useLastRun, useSpend } from '../lib/queries/shell';
import { fmtDate, humanize } from '../lib/format';
import { statusInfo } from './StatusPill';

const REASON: Record<string, string> = {
  missing_secret: 'no API key yet',
  budget_exhausted: 'budget used up',
  paused: 'paused',
  not_built: 'not built yet',
};

function RunBox({ brandId }: { brandId: string | null }) {
  const { data: run, isPending } = useLastRun(brandId);
  if (!brandId || (!isPending && !run)) {
    return (
      <div className="runbox">
        <span>Last run</span>
        <b>No runs yet</b>
        <span>Scheduled checks start at 05:00 UTC once the brand has keywords or prompts.</span>
      </div>
    );
  }
  if (!run) {
    return (
      <div className="runbox" aria-busy="true">
        <span>Last run</span>
        <b>…</b>
      </div>
    );
  }
  const reason = (run.result as { reason?: string } | null)?.reason;
  const status = statusInfo(run.status).label.toLowerCase();
  const tone = run.status === 'failed' ? 'b-n' : run.status === 'skipped' ? 'w-n' : '';
  return (
    <div className="runbox">
      <span>Last run</span>
      <b className={tone || undefined}>{fmtDate(run.finished_at ?? run.created_at, { time: true, year: false })}</b>
      <span>
        {humanize(run.kind)}, {status}
        {run.status === 'skipped' && reason ? `: ${REASON[reason] ?? humanize(reason).toLowerCase()}` : ''}
      </span>
    </div>
  );
}

/** Who is signed in, and the way out. Lives in the sidebar, so the phone menu has it too. */
function SignOut() {
  const { signOut } = useAuth();
  const user = useSessionUser();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="who">
      <span className="em" title={user?.email}>
        {user?.email}
      </span>
      <button
        type="button"
        className="btn sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await signOut();
          } finally {
            setBusy(false);
            navigate('/login', { replace: true });
          }
        }}
      >
        {busy ? 'Signing out…' : 'Sign out'}
      </button>
    </div>
  );
}

function NavItem({ page, brandId, badge, onNavigate }: { page: PageDef; brandId: string | null; badge?: number; onNavigate?: () => void }) {
  if (page.brand && !brandId) {
    return (
      <span className="nav off" aria-disabled="true" title="Add a brand first">
        <Icon name={page.icon} />
        {page.nav}
      </span>
    );
  }
  return (
    <NavLink
      to={pathFor(page.key, brandId)}
      end={page.end}
      className={({ isActive }) => `nav${isActive ? ' on' : ''}`}
      onClick={onNavigate}
    >
      <Icon name={page.icon} />
      {page.nav}
      {badge ? (
        <span className="badge b" aria-label={`${badge} need${badge === 1 ? 's' : ''} attention`}>
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}

/**
 * The sidebar: product mark, brand switcher, the five spec §9 groups, and at the bottom the
 * month's spend per layer and the last job run for the current brand.
 */
export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { brand } = useBrand();
  const brandId = brand?.id ?? null;
  const attention = useAttentionCount(brandId).data ?? 0;
  const spend = useSpend(brand);

  return (
    <aside className="side" aria-label="Sections" id="sidebar">
      <div className="brand">
        <span className="logo">
          <Icon name="logo" />
        </span>
        SEO HQ
      </div>
      <BrandSwitcher />
      <nav aria-label="Main" className="snav">
        {NAV_GROUPS.map((group) => (
          <div className="sgroup" key={group}>
            <div className="slabel">
              <i />
              {group}
            </div>
            {PAGES.filter((p) => p.group === group).map((p) => (
              <NavItem
                key={p.key}
                page={p}
                brandId={brandId}
                badge={p.key === 'opportunities' ? attention : undefined}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ))}
      </nav>
      <div className="sfoot">
        {spend.layers ? (
          <div className="meterbox">
            <SpendMeter title="Spend this month" layers={spend.layers} />
          </div>
        ) : null}
        <RunBox brandId={brandId} />
        <SignOut />
      </div>
    </aside>
  );
}
